import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { UI, LANG } from "../data/content";
import { ContactModule, usePageMeta } from "../components/kit";
import geo from "../data/explorerGeo.json";

const X = () => UI.explorer;
const COLORS = { 80: "#071F35", 70: "#08243E", 60: "#0A2E4D", 50: "#10456F", 45: "#2A5C8A", 40: "#3A6E9E", 35: "#5D89B4", 25: "#7FA6C9", 15: "#C0D4E6" };
const colorFor = (v) => (v == null ? "var(--line)" : COLORS[v] || "#10456F");
const UPLIFT = { large: 0, medium: 10, small: 20 };

const pathBBox = (paths) => {
  let minx = 1e9, miny = 1e9, maxx = -1e9, maxy = -1e9;
  paths.forEach(({ d }) => {
    (d.match(/-?\d+\.?\d*/g) || []).forEach((n, i) => {
      const v = parseFloat(n);
      if (i % 2 === 0) { minx = Math.min(minx, v); maxx = Math.max(maxx, v); }
      else { miny = Math.min(miny, v); maxy = Math.max(maxy, v); }
    });
  });
  return [minx - 2, miny - 2, maxx - minx + 4, maxy - miny + 4];
};

const RegionMap = ({ paths, values, selected, onSelect, testPrefix }) => {
  const vb = useMemo(() => pathBBox(paths), [paths]);
  return (
    <svg viewBox={vb.join(" ")} className="w-full h-auto" role="group" aria-label={X().mapAria}>
      {paths.map((p) => (
        <path key={p.id} d={p.d} fill={colorFor(values[p.id])} stroke="var(--paper)" strokeWidth="0.7"
          opacity={selected && selected !== p.id ? 0.45 : 1}
          className="cursor-pointer transition-opacity focus:outline-none"
          tabIndex={0} role="button" aria-label={LANG === "el" ? p.el : p.en}
          data-testid={`${testPrefix}-${p.id}`}
          onClick={() => onSelect(p.id)}
          onKeyDown={(e) => e.key === "Enter" && onSelect(p.id)}>
          <title>{LANG === "el" ? p.el : p.en}</title>
        </path>
      ))}
    </svg>
  );
};

export default function Explorer() {
  usePageMeta(`${X().title} | KairosHK`, X().lead);
  const [progs, setProgs] = useState([]);
  const [country, setCountry] = useState(null);
  const [type, setType] = useState("");
  const [ben, setBen] = useState("");
  const [theme, setTheme] = useState("");
  const [q, setQ] = useState("");
  const [size, setSize] = useState("large");
  const [region, setRegion] = useState(null);
  const [table, setTable] = useState(false);
  useEffect(() => { api.get("/funding/explorer-data").then(({ data }) => setProgs(data.programmes)).catch(() => {}); }, []);

  const el = LANG === "el";
  const tax = (grp, v) => (el ? (geo.TAX[grp] || {})[v] || v : v);
  const pName = (p) => (el ? p.el : p.n);
  const pAuth = (p) => (el ? (geo.ELP[p.n] || [])[1] || p.a : p.a);
  const pDesc = (p) => (el ? (geo.ELP[p.n] || [])[2] || p.d : p.d);

  const filtered = progs.filter((p) =>
    (!country || p.c === country) && (!type || p.t === type) && (!ben || p.b.includes(ben)) && (!theme || p.th.includes(theme)) &&
    (!q || (p.n + p.el + p.d + ((geo.ELP[p.n] || [])[2] || "")).toLowerCase().includes(q.toLowerCase())));
  const counts = { GR: filtered.filter((p) => p.c === "GR").length, CY: filtered.filter((p) => p.c === "CY").length, EU: filtered.filter((p) => p.c === "EU").length };
  const types = [...new Set(progs.map((p) => p.t))].sort();
  const bens = [...new Set(progs.flatMap((p) => p.b))].sort();
  const themes = [...new Set(progs.flatMap((p) => p.th))].sort();
  const typeCounts = types.map((t) => [t, progs.filter((p) => p.t === t && (!country || p.c === country)).length]).sort((a, b) => b[1] - a[1]);
  const maxT = Math.max(...typeCounts.map(([, n]) => n), 1);

  const regionVals = {};
  geo.GEO.gr.forEach((p) => {
    const r = geo.REGIONS.find((x) => x.id === p.id);
    regionVals[p.id] = r && r.v != null ? Math.min(r.v + UPLIFT[size], 80) : null;
  });
  const selRegion = region && geo.REGIONS.find((r) => r.id === region);
  const selGeo = region && geo.GEO.gr.find((g) => g.id === region);
  const grouped = region ? {
    regional: progs.filter((p) => p.n.startsWith("Regional Programmes")),
    territorial: progs.filter((p) => p.c === "GR" && p.n.includes("LEADER")),
    national: progs.filter((p) => p.c === "GR" && !p.n.startsWith("Regional Programmes") && !p.n.includes("LEADER")),
    eu: progs.filter((p) => p.c === "EU"),
  } : null;

  const chip = (active) => `topic-tag !py-2 !px-4 cursor-pointer border-0 transition-colors ${active ? "!bg-[var(--accent)] !text-white" : ""}`;

  return (
    <div data-testid="explorer-page">
      <section className="k-section !pb-10">
        <div className="k-container">
          <p className="eyebrow mb-4">{X().eyebrow}</p>
          <h1 className="k-h1 mb-5">{X().title}</h1>
          <p className="standfirst max-w-3xl mb-6">{X().lead}</p>
          <div className="flex flex-wrap gap-x-12 gap-y-4 border-t border-b border-line py-6" data-testid="explorer-stats">
            {[[filtered.length, X().indexed], [counts.GR, X().gr], [counts.CY, X().cy], [counts.EU, X().eu]].map(([n, l], i) => (
              <div key={l}><p className={`text-4xl font-semibold ${i === 2 ? "text-[var(--accent)]" : "text-brand"}`} style={{ fontVariantNumeric: "tabular-nums" }}>{n}</p><p className="text-xs uppercase tracking-widest text-ink-muted mt-1">{l}</p></div>
            ))}
          </div>
          <p className="text-xs text-ink-muted mt-4 max-w-3xl" data-testid="explorer-indicative-note">{X().note}</p>
        </div>
      </section>

      <section className="k-section !py-12 bg-paper-alt" data-testid="explorer-filters">
        <div className="k-container">
          <div className="flex flex-wrap gap-2 mb-6">
            <button onClick={() => setCountry(null)} className={chip(!country)} data-testid="xf-country-all">{X().all}</button>
            {["GR", "CY", "EU"].map((c) => (
              <button key={c} onClick={() => setCountry(country === c ? null : c)} className={chip(country === c)} data-testid={`xf-country-${c}`}>{X()[c.toLowerCase()]}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <input className="field-input" placeholder={X().search} value={q} onChange={(e) => setQ(e.target.value)} data-testid="xf-search" />
            {[[type, setType, types, "type", X().type], [ben, setBen, bens, "ben", X().beneficiary], [theme, setTheme, themes, "th", X().theme]].map(([val, set, opts, grp, label]) => (
              <select key={grp} className="field-input" value={val} onChange={(e) => set(e.target.value)} data-testid={`xf-${grp}`}>
                <option value="">{label}</option>
                {opts.map((o) => <option key={o} value={o}>{tax(grp, o)}</option>)}
              </select>
            ))}
          </div>
          <div className="max-w-2xl" data-testid="explorer-type-chart">
            {typeCounts.map(([t, n]) => (
              <button key={t} onClick={() => setType(type === t ? "" : t)} className="flex items-center gap-3 w-full bg-transparent border-0 cursor-pointer py-1 text-left group">
                <span className={`text-xs w-44 shrink-0 truncate ${type === t ? "font-semibold text-brand" : "text-ink-muted"}`}>{tax("type", t)}</span>
                <span className="h-3 transition-all group-hover:opacity-80" style={{ width: `${(n / maxT) * 100}%`, minWidth: 3, background: type === t ? "var(--accent)" : "var(--brand)" }} />
                <span className="text-xs text-ink-muted" style={{ fontVariantNumeric: "tabular-nums" }}>{n}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="k-section !py-14" data-testid="explorer-map-section">
        <div className="k-container">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
            <div><p className="eyebrow mb-3">{X().mapEyebrow}</p><h2 className="k-h2">{X().mapTitle}</h2><div className="section-rule" /></div>
            <div className="flex gap-2">
              {["large", "medium", "small"].map((s) => (
                <button key={s} onClick={() => setSize(s)} className={chip(size === s)} data-testid={`size-${s}`}>{X().sizes[s]}</button>
              ))}
            </div>
          </div>
          <div className="grid lg:grid-cols-5 gap-10 mt-8 items-start">
            <div className="lg:col-span-2" data-testid="map-greece"><RegionMap paths={geo.GEO.gr} values={regionVals} selected={region} onSelect={(id) => setRegion(region === id ? null : id)} testPrefix="region" /></div>
            <div className="lg:col-span-3 border border-line p-6 min-h-[260px]" data-testid="region-panel">
              {!selRegion ? (
                <p className="text-ink-muted text-sm">{X().pickRegion}</p>
              ) : (
                <>
                  <h3 className="k-h3 mb-1">{el ? selRegion.n : selRegion.en}</h3>
                  <p className="text-3xl font-semibold text-brand mb-1" style={{ fontVariantNumeric: "tabular-nums" }} data-testid="region-intensity">
                    {Math.min(selRegion.v + UPLIFT[size], 80)}%
                  </p>
                  <p className="text-xs text-ink-muted mb-3">{selRegion.v}% {X().base} + {UPLIFT[size]} {X().uplift} · {X().sizes[size]}</p>
                  {selRegion.note && <p className="text-xs text-ink-muted border-l-2 border-[var(--accent)] pl-3 mb-4">{selRegion.note}</p>}
                  {grouped && [["regional", X().gRegional], ["national", X().gNational], ["territorial", X().gTerritorial], ["eu", X().gEU]].map(([k, label]) =>
                    grouped[k].length > 0 && (
                      <div key={k} className="mb-3">
                        <p className="text-[11px] uppercase tracking-widest text-ink-muted mb-1">{label} ({grouped[k].length})</p>
                        <p className="text-xs leading-relaxed">{grouped[k].slice(0, 6).map(pName).join(" · ")}{grouped[k].length > 6 ? " …" : ""}</p>
                      </div>
                    ))}
                </>
              )}
              {selGeo && null}
            </div>
          </div>
          <p className="text-[11px] text-ink-muted mt-6" data-testid="map-source-note">{X().mapSource}</p>
        </div>
      </section>

      <section className="k-section !py-14 bg-paper-alt" data-testid="explorer-results">
        <div className="k-container">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div><p className="eyebrow mb-3">{X().resultsEyebrow}</p><h2 className="k-h2">{X().results} ({filtered.length})</h2><div className="section-rule" /></div>
            <button onClick={() => setTable(!table)} className="btn-secondary !py-2 !px-4 text-xs" data-testid="toggle-table-btn">{table ? X().cardView : X().tableView}</button>
          </div>
          {table ? (
            <table className="k-table" data-testid="explorer-table">
              <thead><tr><th>{X().programme}</th><th>{X().type}</th><th>{X().beneficiary}</th><th>{X().intensity}</th><th>{X().status}</th></tr></thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.n}><td className="font-semibold">{pName(p)}</td><td>{tax("type", p.t)}</td><td>{p.b.map((b) => tax("ben", b)).join(", ")}</td><td>{tax("int", p.i)}</td><td>{tax("sta", p.s)}</td></tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <article key={p.n} className="cap-card" data-testid={`xp-card-${p.n.slice(0, 20).replace(/\W+/g, "-")}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className="topic-tag">{X()[p.c.toLowerCase()]}</span>
                    <span className="text-[11px] uppercase tracking-widest text-ink-muted">{tax("sta", p.s)}</span>
                  </div>
                  <h3 className="cap-title text-base font-semibold leading-snug mb-0.5">{pName(p)}</h3>
                  {!el && p.el !== p.n && <p className="text-xs text-ink-muted mb-2">{p.el}</p>}
                  <p className="text-xs text-ink-muted mb-3">{pAuth(p)}</p>
                  <p className="text-sm text-ink-muted leading-relaxed flex-1">{pDesc(p)}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-xs text-ink-muted border-t border-line pt-3">
                    <span className="font-semibold text-brand">{tax("int", p.i)}</span>
                    <span>{tax("type", p.t)}</span>
                    <span>{p.th.slice(0, 3).map((t) => tax("th", t)).join(" · ")}</span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
      <section className="k-container py-16"><ContactModule /></section>
    </div>
  );
}
